<template>
    <DModal
        :id="smsModalId"
        :show="showModal"
        :title="__(`tools.sms.samples.${smsType}.${smsType}`)"
        close-color-secondary
        is-big
        overflow-auto
        @close="closeModal"
    >
        <DTabs>
            <DTabItem
                :label="__('tools.sms.preview_modal.tabs.plain_text')"
                :active="currentTab === 0"
                @click="changeTab(0)"
            />
            <DTabItem
                :label="__('tools.sms.preview_modal.tabs.send_copy')"
                :active="currentTab === 1"
                @click="changeTab(1)"
            />
        </DTabs>

        <DCard
            v-if="currentTab === 0"
        >
            <!-- eslint-disable vue/no-v-html -->
            <div
                v-if="contentData !== null"
                class="m-5 content-data"
                v-html="contentData"
            />
            <!--eslint-enable-->
            <div
                v-if="errorsString !== null"
                class="text-danger m-5"
            >
                {{ errorsString }}
            </div>
        </DCard>

        <DCard
            v-if="currentTab === 1"
            class="p-2"
        >
            <DFormRow
                class="mb-2"
                is-vertical
                :label="__('tools.sms.preview_modal.phone_number')"
            >
                <DInput
                    id="phone_number"
                    v-model="form.phoneNumber"
                    @update:value="validatePhoneNumber"
                />
            </DFormRow>
            <DButton
                :label="__('tools.sms.preview_modal.send_copy')"
                :disabled="!phoneNumberValid"
                class="my-2"
                color="primary"
                @click="submitSendCopy"
            />
            <div
                v-if="messageSent !== null"
            >
                <p
                    v-if="messageSent === true"
                    class="text-success text-center"
                >
                    Message sent!
                </p>
                <p
                    v-else
                    class="text-danger text-center"
                >
                    {{ sendMessageError }}
                </p>
            </div>
        </DCard>

        <template #actions>
            <DButton
                :label="__('messages.save')"
                color="primary"
                :disabled="!previewCompleted"
                @click="handleSaveClick"
            />
        </template>
    </DModal>
    <DModal
        v-model:show="showSaveConfirmationModal"
        :title="__('tools.sms.preview_modal.confirmation_modal_title')"
        is-confirm
        :message="__('tools.sms.preview_modal.confirmation_modal_message')"
        @confirm="saveForm"
        @close="showSaveConfirmationModal=false"
    />
</template>

<script setup>
import { ref } from 'vue'

import { route } from '~@/Composables/route'
import { useForm } from '@inertiajs/vue3'
import { isValidPhoneNumber } from 'libphonenumber-js'

const emit = defineEmits(['closeModal', 'submit'])

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    smsType: {
        type: String,
        default: ''
    },
    content: {
        type: String,
        default: ''
    }
})

const form = useForm({
    content: props.content,
    smsType: props.smsType,
    phoneNumber: null
})

const phoneNumberValid = ref(false)
const messageSent = ref(null)
const sendMessageError = ref(null)

const currentTab = ref(0)
const smsModalId = 'smsModalId'
const previewCompleted = ref(false)
const contentData = ref(null)
const errorsString = ref(null)
const showSaveConfirmationModal = ref(false)

function changeTab (tabIndex) {
    currentTab.value = tabIndex
}

function closeModal () {
    previewCompleted.value = false
    contentData.value = null
    errorsString.value = null
    currentTab.value = 0
    emit('closeModal')
}

function getContent () {
    window.axios.get(route('tools.sms-templates.preview', {
        smsType: props.smsType,
        content: props.content
    }))
        .then(response => {
            contentData.value = nl2br(response.data.content)
            previewCompleted.value = true
        })
        .catch((error) => {
            const errors = []
            const errorObject = error.response && error.response.data && error.response.data.errors
                ? error.response.data.errors
                : {}
            Object.keys(errorObject).forEach((field) => {
                errorObject[field].forEach((message) => {
                    errors.push(message)
                })
            })

            errorsString.value = errors.join(', ')
        })
}

function nl2br (content) {
    return content.replace(/\n/g, '<br>')
}

function handleSaveClick () {
    showSaveConfirmationModal.value = true
}

function saveForm () {
    emit('submit')
    showSaveConfirmationModal.value = false
}

function validatePhoneNumber () {
    phoneNumberValid.value = isValidPhoneNumber(form.phoneNumber)
}

function submitSendCopy () {
    window.axios.get(route('tools.sms-templates.send-copy', form))
        .then(() => {
            messageSent.value = true
        })
        .catch((error) => {
            sendMessageError.value = error.response.data.errors.general[0]
            messageSent.value = false
        })
}

getContent()
</script>

<style>
.content-data {
    min-height: 6vh;
    min-width: 30vw;
}
</style>
